/*
 * Created Date: Thu, 4th Feb 2021, 09:36:45 am
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2021 The Distance
 */

import React, { Fragment, useState, useEffect } from 'react';
import { Admin, Resource } from 'react-admin';
import { Provider } from 'react-redux';
import { createBrowserHistory as createHistory } from 'history';
import { createStore } from 'utils';
import defaultMessages from 'ra-language-english';
import i18nProvider from 'i18n';
import { CircularProgress } from '@material-ui/core';
import cloudAuthProvider from 'AuthProvider/AmplifyAuthProvider/AuthProvider';
import buildProvider from './DataProvider';
import { Login } from 'Layout/Auth';
import defaultTheme, { darkTheme, lightTheme } from 'theme';
import { appStyles } from 'styles';
import customRoutes from './routes';
import { setTabbedFilterFieldReducer } from 'reducers';
import CustomLayout from 'Layout';
import {
  AdminResource,
  HelpResource,
  EvidenceResource,
  PlotResource,
  PlotSuspect,
  PlotEvidence,
  EventResource,
  EventEvidence,
  EventSuspect,
  EventSleuth,
  LocationResource,
  TemplateResource,
  FeedbackResource,
  UserResource,
} from 'Resources';

const history = createHistory();
const stage = 'master' || 'local';
const commit = 'c7d23484790dc4687d07d7c2267a8c9ceee8a84e' || 'dev';

const authProvider = cloudAuthProvider();

const App = () => {
  const classes = appStyles();
  const [dataProvider, setDataProvider] = useState(null);
  const [store, setStore] = useState(null);

  useEffect(() => {
    const customReducers = { userTabFilters: setTabbedFilterFieldReducer };

    const initialiseApp = async () => {
      return buildProvider().then((dataProvider) => {
        const store = createStore({
          authProvider,
          dataProvider,
          history,
          customReducers,
          i18nProvider: () => defaultMessages,
          initialState: {},
        });
        window.__getStore = () => store;
        setDataProvider(() => dataProvider);
        return setStore(store);
      });
    };
    initialiseApp();
  }, []);

  // ***** STYLES *****
  if (!(dataProvider && store)) {
    return (
      <div className={classes.loading}>
        {'Loading...'}
        <CircularProgress size={60} className={classes.circularProgress} />
      </div>
    );
  }

  // ***** FUNCTIONS *****
  // ***** RENDER *****
  return (
    <Provider store={store}>
      <Fragment>
        <Admin
          disableTelemetry
          history={history}
          authProvider={authProvider}
          dataProvider={dataProvider}
          title="The Core"
          loginPage={Login}
          i18nProvider={i18nProvider}
          theme={defaultTheme} //comment this out if using ra-enterprise Admin component
          lightTheme={lightTheme}
          darkTheme={darkTheme}
          customRoutes={customRoutes}
          layout={CustomLayout}
        >
          {/* permissions prop will contain an array of permissions from the AuthProvider e.g. ["SUPER"] */}
          {(permissions) => [
            <Resource {...EvidenceResource(permissions)} />,
            <Resource {...PlotResource(permissions)} />,
            <Resource {...AdminResource(permissions)} />,
            <Resource {...HelpResource(permissions)} />,
            <Resource {...EventResource(permissions)} />,
            <Resource {...LocationResource(permissions)} />,
            <Resource { ...TemplateResource(permissions)}/>,
            <Resource {...EventEvidence(permissions)} />,
            <Resource {...FeedbackResource(permissions)} />,
            <Resource {...UserResource(permissions)} />,
            // hidden resources
            <Resource {...PlotEvidence} />,
            <Resource {...PlotSuspect} />,
            <Resource {...EventSuspect} />,
            <Resource {...EventSleuth} />,
          ]}
        </Admin>
        {/* footer to display branch and commit id in Amplify for test purposes */}
        {stage &&
        !(
          stage.startsWith('prod') ||
          stage === 'master' ||
          stage === 'client-staging'
        ) ? (
          <div className={classes.devCommit}>
            {stage} {commit.substring(0, 4)}
          </div>
        ) : null}
      </Fragment>
    </Provider>
  );
};

export default App;
